import { Container, Sprite, Texture } from "pixi.js"
import { Coordinate } from "game-common/models"
import { contentSheetResource } from "../content_textures"
import { arrowScale } from "../sprites/bullet"
import { ARROW_HEAD_PX, arrowPiece, arrowTuft, standingShaft } from "../sprites/landed_arrow_pose"

/**
 * A SPENT ARROW THAT BROKE (combat/spent-arrows): one in five, there is no pickup, so the ground shows it
 * snapped where it came down and then lets it go. Two pieces of the arrow's own art -- a stub posed as a
 * lasting arrow would stand (the shaft just behind its buried head), and the fletched half lying flat
 * beside it -- held, then faded. A broken arrow that glanced off a blocker (pitch 0) lies in two pieces.
 *
 * Driven by the server's `arrow-broken` tile effect, so it is not an entity: nothing to click, collect or
 * glint. Every viewer is told the same heading and slant, and the fletched half's turn is hashed from the
 * point, so they all see the same pieces.
 */

const ARROW_SPRITE = "arrow-brown.png"
// How much shaft the stub keeps behind the head, in art pixels.
const STUB_PX = 8
const HOLD_MS = 1500
const FADE_MS = 2000
const MAX_BROKEN = 24

const arrowTexture = (): Texture | undefined => contentSheetResource("humanoid")?.textures?.[ARROW_SPRITE]

/** A stable number in [0, 1) from a point, so every viewer turns the loose half alike. */
const pointHash = (at: Coordinate): number => {
    let h = Math.imul(Math.round(at.x) ^ 0x9e3779b1, 0x85ebca6b) ^ Math.imul(Math.round(at.y) ^ 0x7f4a7c15, 0xc2b2ae35)
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13
    return (h >>> 0) / 4294967296
}

export class BrokenArrows {
    private broken: { art: Container; start: number }[] = []

    emit(surface: Container, at: Coordinate, heading = 0, pitchDegrees = 0, texture = arrowTexture(),
        now = performance.now()): void {
        if (!texture) return
        const width = texture.frame.width
        const split = width - ARROW_HEAD_PX - STUB_PX
        // Standing: the shaft behind the buried head. Lying (it glanced off something): the head end whole.
        const stub = arrowPiece(texture, split, pitchDegrees > 0 ? width - ARROW_HEAD_PX : width)
        const loose = arrowPiece(texture, 0, split)
        if (!stub || !loose) return
        if (this.broken.length >= MAX_BROKEN) this.broken.shift()?.art.destroy({ children: true })
        const scale = arrowScale()
        const art = new Container()
        art.position.set(at.x, at.y)
        art.zIndex = 100 + at.y

        const standing = new Sprite(stub)
        if (pitchDegrees > 0) {
            const shaft = standingShaft(heading, pitchDegrees)
            standing.anchor.set(1, 0.5)
            standing.scale.set(scale * shaft.lengthScale, scale)
            standing.rotation = shaft.rotation
        } else {
            standing.anchor.set(0.5, 0.5)
            standing.scale.set(scale)
            standing.rotation = heading
        }

        // The fletched half lies flat a little to one side, turned well across the line of flight.
        const r = pointHash(at), side = r < .5 ? -1 : 1
        const lying = new Sprite(loose)
        lying.anchor.set(0.5, 0.5)
        lying.scale.set(scale)
        lying.rotation = heading + side * (Math.PI / 3 + r * Math.PI / 3)
        const across = heading + side * Math.PI / 2
        lying.position.set(Math.cos(across) * 5 - Math.cos(heading) * 4, Math.sin(across) * 5 - Math.sin(heading) * 4)

        art.addChild(lying, standing)
        if (pitchDegrees > 0) art.addChild(arrowTuft())
        surface.addChild(art)
        this.broken.push({ art, start: now })
    }

    /**
     * An arrow struck into a cliff's face (openspec world/cliffs): whole, as it flew along `heading`, its head in the rock
     * at `at` drawn `lift` up the face, held and faded as a broken one is. Nothing comes of it: it is lost.
     */
    stick(surface: Container, at: Coordinate, heading = 0, lift = 0, texture = arrowTexture(), now = performance.now()): void {
        if (!texture) return
        const shaft = arrowPiece(texture, 0, texture.frame.width - ARROW_HEAD_PX)
        if (!shaft) return
        if (this.broken.length >= MAX_BROKEN) this.broken.shift()?.art.destroy({ children: true })
        const art = new Container()
        art.position.set(at.x, at.y - lift)
        art.zIndex = 100 + at.y
        const stuck = new Sprite(shaft)
        // The head's end at the face, the fletching out toward the archer.
        stuck.anchor.set(1, 0.5)
        stuck.scale.set(arrowScale())
        stuck.rotation = heading
        art.addChild(stuck)
        surface.addChild(art)
        this.broken.push({ art, start: now })
    }

    update(now = performance.now()): void {
        this.broken = this.broken.filter(({ art, start }) => {
            if (art.destroyed) return false
            const t = now - start
            if (t >= HOLD_MS + FADE_MS) { art.destroy({ children: true }); return false }
            art.alpha = t <= HOLD_MS ? 1 : 1 - (t - HOLD_MS) / FADE_MS
            return true
        })
    }

    clear(): void {
        for (const { art } of this.broken) if (!art.destroyed) art.destroy({ children: true })
        this.broken = []
    }
}
